import type { CommercialProposalContent } from './content.js';
import type { CommercialProposalTotalCurrency } from './content.js';
import type { CommercialProposalCalculationOutput } from './calculation.js';
import type { CommercialProposalPublicDto, CommercialProposalPublicLine } from './public.js';
import type { CommercialProposalPaymentScheme } from './status.js';

// WHY: deterministic display formatting moved here from the backend public
// projection so packages/shared is the single source of truth for the
// customer-facing sheet rows + document chrome. Grouped with a thin space and
// a dot decimal so the sheet matches the prototype; the structural numbers
// already crossed the wire as canonical strings/numbers, these are display
// only and never re-parsed back into money math. The THIN_SPACE byte is the
// U+202F NARROW NO-BREAK SPACE and MUST stay byte-identical so output is stable.

const THIN_SPACE = ' ';

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, THIN_SPACE);
}

function fixed2(value: number | string): string {
  const normalized = typeof value === 'string' ? value : value.toFixed(2);
  const [int, frac] = normalized.split('.');
  return `${group(int ?? '0')}.${frac ?? '00'}`;
}

function rateRatio(rate: number): { numerator: bigint; denominator: bigint } {
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new RangeError('total currency conversion rate must be finite and positive');
  }
  const match = /^(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(String(rate));
  if (!match) throw new RangeError('total currency conversion rate must be decimal');
  const whole = match[1] ?? '0';
  const fraction = match[2] ?? '';
  const exponent = Number(match[3] ?? '0') - fraction.length;
  const coefficient = BigInt(`${whole}${fraction}`);
  return exponent >= 0
    ? { numerator: coefficient * 10n ** BigInt(exponent), denominator: 1n }
    : { numerator: coefficient, denominator: 10n ** BigInt(-exponent) };
}

function roundedQuotient(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n;
  const absolute = negative ? -numerator : numerator;
  const quotient = absolute / denominator;
  const remainder = absolute % denominator;
  const rounded = remainder * 2n >= denominator ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}

function centsToMoney(cents: bigint): string {
  const negative = cents < 0n;
  const digits = (negative ? -cents : cents).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

function convertRub(rub: string, rate: number): string {
  const amountCents = BigInt(rub.replace('.', ''));
  const ratio = rateRatio(rate);
  return centsToMoney(roundedQuotient(amountCents * ratio.denominator, ratio.numerator));
}

function num(value: number): string {
  // Drop trailing zeros for plain quantities (weight/volume) while keeping
  // up to 3 fraction digits, then group the integer part.
  const rounded = Number(value.toFixed(3));
  const [int, frac] = String(rounded).split('.');
  return frac ? `${group(int ?? '0')}.${frac}` : group(int ?? '0');
}

function rubMoney(value: number | string): string {
  return `${fixed2(value)}${THIN_SPACE}₽`;
}

function usd(value: number | string): string {
  return `${fixed2(value)}${THIN_SPACE}$`;
}

function cny(value: number | string): string {
  return `${fixed2(value)}${THIN_SPACE}¥`;
}

function totalMoney(
  rub: string,
  currency: CommercialProposalTotalCurrency,
  content: Pick<CommercialProposalContent, 'cnyRateRub' | 'usdRateRub'>,
): string {
  if (currency === 'CNY') return cny(convertRub(rub, content.cnyRateRub));
  if (currency === 'USD') return usd(convertRub(rub, content.usdRateRub));
  return rubMoney(rub);
}

function usdPerKg(value: number | string): string {
  return `${fixed2(value)}${THIN_SPACE}$ / кг`;
}

function usdPerM3(value: number | string): string {
  return `${fixed2(value)}${THIN_SPACE}$ / м³`;
}

function pct(value: number): string {
  return `${num(value)}%`;
}

/**
 * Customer-facing document chrome (brand, section captions, signature).
 * Display copy only — never part of the wire contract's structural shape.
 */
export const COMMERCIAL_PROPOSAL_SHEET_CHROME = {
  brand: 'Magistral Import',
  title: 'Коммерческое предложение',
  buyoutCaption: 'Выкуп товара',
  buyoutNotesHeader: 'Условия — выкуп',
  logisticsNotesHeader: 'Условия — логистика',
  grandCaption: 'Итого: выкуп + доставка',
  /// Grand-band caption when only ONE section is active (#1410): no
  /// «выкуп + доставка» subtitle, just the single total.
  grandCaptionSingle: 'Итого',
  signatureCaption: 'Менеджер',
} as const;

/**
 * Static customer-facing contact channels shown at the foot of the КП document
 * (web sheet, operator preview, and PDF): heading, links, and the a11y new-tab
 * hint appended to each link's aria-label. Brand-wide chrome — never per-proposal
 * data, so it stays outside the public DTO/wire contract.
 */
export const COMMERCIAL_PROPOSAL_CONTACT_LINKS = {
  heading: 'Мы всегда на связи',
  newTabHint: 'откроется в новой вкладке',
  links: [
    { label: 'Telegram', url: 'https://t.me/imagistral' },
    {
      label: 'Авито',
      url: 'https://www.avito.ru/brands/magistralimport/all/predlozheniya_uslug?sellerId=398b574aeccea0ae592fbbdd1faa6c62',
    },
    { label: 'Сайт', url: 'https://magimp.ru/' },
  ],
} as const;

/**
 * Payment-scheme badge labels shown near the proposal header (customer copy).
 */
export const COMMERCIAL_PROPOSAL_PAYMENT_SCHEME_BADGE: Record<
  CommercialProposalPaymentScheme,
  string
> = {
  vat: 'Оплата: р/с с НДС + УПД',
  novat: 'Оплата: р/с без НДС + УПД',
  cash: 'Оплата: наличными',
  crypto: 'Оплата: криптовалюта',
  card: 'Оплата: на карту',
};

/**
 * Payment-scheme NAME used as the label of the rouble pay-rows in the buyout
 * and logistics line groups (customer copy).
 */
export const COMMERCIAL_PROPOSAL_PAYMENT_SCHEME_NAME: Record<
  CommercialProposalPaymentScheme,
  string
> = {
  vat: 'Оплата через р/с с НДС + УПД',
  novat: 'Оплата через р/с без НДС + УПД',
  cash: 'Оплата наличными',
  crypto: 'Оплата криптовалютой',
  card: 'Оплата на карту',
};

/**
 * Build the validity sentence shown under the totals. Pure — formats the
 * already-frozen proposal date; never reads a clock.
 */
export function commercialProposalValiditySentence(proposalDate: string): string {
  const d = proposalDate.trim();
  return d
    ? `Предложение действительно на дату выставления — ${d}. Расчёт по курсу на день оплаты.`
    : 'Предложение действительно на дату выставления. Расчёт по курсу на день оплаты.';
}

function buildBuyoutLines(
  content: CommercialProposalContent,
  calculation: CommercialProposalCalculationOutput,
): CommercialProposalPublicLine[] {
  const lines: CommercialProposalPublicLine[] = [
    { label: 'Стоимость товара в юанях', value: cny(content.goodsCny) },
  ];
  // The prototype hides the China-delivery row entirely when it is zero.
  if (content.chinaDeliveryCny !== 0) {
    lines.push({ label: 'Доставка по Китаю', value: cny(content.chinaDeliveryCny) });
  }
  lines.push(
    {
      label: `Услуги всё под ключ (${pct(content.servicePercent)})`,
      value: cny(calculation.serviceCny),
    },
    { label: 'Итого в юанях', value: cny(calculation.totalCny), tone: 'total' },
    {
      label: COMMERCIAL_PROPOSAL_PAYMENT_SCHEME_NAME[content.paymentScheme],
      value: totalMoney(calculation.buyRub, content.buyoutTotalCurrency, content),
      tone: 'pay',
    },
  );
  return lines;
}

function buildLogisticsLines(
  content: CommercialProposalContent,
  calculation: CommercialProposalCalculationOutput,
): CommercialProposalPublicLine[] {
  return [
    { label: 'Вес', value: `${num(content.weightKg)} кг` },
    { label: 'Объём', value: `${num(content.volumeM3)} м³` },
    {
      label: 'Тариф на перевозку',
      value:
        content.tariffUnit === 'm3'
          ? usdPerM3(content.tariffUsdPerKg)
          : usdPerKg(content.tariffUsdPerKg),
    },
    { label: 'Стоимость перевозки', value: usd(calculation.transportUsd) },
    { label: `Страховка (${pct(content.insurancePercent)})`, value: usd(calculation.insuranceUsd) },
    { label: 'Упаковка на складе', value: usd(content.packagingUsd) },
    { label: 'Погрузочно-разгрузочные работы', value: usd(content.prrUsd) },
    {
      label: 'Общая стоимость логистики',
      value: usd(calculation.logisticsUsd),
      tone: 'total',
    },
    {
      label: COMMERCIAL_PROPOSAL_PAYMENT_SCHEME_NAME[content.paymentScheme],
      value: totalMoney(calculation.logisticsRub, content.logisticsTotalCurrency, content),
      tone: 'pay',
    },
  ];
}

/**
 * @aiPurpose shared single source of truth for the customer-facing КП sheet:
 *   builds the ordered buyout/logistics rows + document chrome the public web
 *   preview and the backend public projection both consume. PURE — no I/O, no
 *   clock, no randomness.
 * @aiSecurity load-bearing data boundary. CONSUMES `calculation` outputs
 *   verbatim and only formats — it NEVER recomputes the formula and NEVER
 *   reads an internal coefficient (no-VAT / VAT divisor, resolved
 *   paymentDivisor, insurance rate), object key, token, or owner/internal id.
 *   It exposes only customer-visible data; the backend re-parses the result
 *   through the `.strict()` public schema as the mechanical leak guard.
 */
export function buildCommercialProposalSheetModel(
  content: CommercialProposalContent,
  calculation: CommercialProposalCalculationOutput,
  opts: { hasPhoto: boolean },
): CommercialProposalPublicDto {
  // #1410: the DTO SHAPE is unchanged — no `mode` field is added. An inactive
  // section is BLANKED (empty rows + empty term/notes/total) so the hidden
  // section's term/notes never reach the customer-facing sheet. The grand total
  // is always shown (it already sums only the active section[s]).
  const buyoutActive = content.mode !== 'LOGISTICS';
  const logisticsActive = content.mode !== 'BUYOUT';
  const logisticsTitleBase = content.destination
    ? `Логистика — ${content.destination}`
    : 'Логистика';
  return {
    proposalDate: content.proposalDate,
    client: content.client,
    cnyRateDisplay: rubMoney(content.cnyRateRub),
    usdRateDisplay: rubMoney(content.usdRateRub),
    productName: content.productName,
    characteristics: content.characteristics,
    hasPhoto: opts.hasPhoto,
    paymentScheme: content.paymentScheme,
    paymentSchemeBadge: COMMERCIAL_PROPOSAL_PAYMENT_SCHEME_BADGE[content.paymentScheme],
    buyoutTerm: buyoutActive ? content.buyoutTerm : '',
    buyoutLines: buyoutActive ? buildBuyoutLines(content, calculation) : [],
    logisticsTitle: logisticsActive ? logisticsTitleBase : '',
    logisticsTerm: logisticsActive ? content.logisticsTerm : '',
    logisticsLines: logisticsActive ? buildLogisticsLines(content, calculation) : [],
    buyTotalDisplay: buyoutActive
      ? totalMoney(calculation.buyRub, content.buyoutTotalCurrency, content)
      : '',
    logisticsTotalDisplay: logisticsActive
      ? totalMoney(calculation.logisticsRub, content.logisticsTotalCurrency, content)
      : '',
    grandTotalDisplay: totalMoney(calculation.grandTotalRub, content.grandTotalCurrency, content),
    validity: commercialProposalValiditySentence(content.proposalDate),
    buyoutNotes: buyoutActive ? content.buyoutNotes : '',
    logisticsNotes: logisticsActive ? content.logisticsNotes : '',
    managerName: content.managerName,
    managerPhone: content.managerPhone,
  };
}
